import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  getHoldBookings,
  getExpiredHoldBookings,
  completeHoldBooking,
  editHoldBooking,
  deleteBooking
} from '../store/slices/bookingSlice'
import { fetchAllUsers } from '../store/slices/authSlice'
import { useSnackbar } from 'notistack'
import { useSidebarContext } from '../scenes/global/sidebar/SidebarContext'
import {
  Clock,
  User,
  MapPin,
  DollarSign,
  Calendar,
  CheckCircle,
  AlertCircle,
  Eye,
  Phone,
  Building,
  Printer,
  Edit,
  Trash2
} from 'lucide-react'
import Swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.min.css'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import printJS from 'print-js'
import api from '../api'

const HoldBookings = () => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const { theme, gradient } = useSidebarContext()

  const { holdBookings, expiredHoldBookings = [], loading } = useSelector(
    state => state.booking
  )
  const { users } = useSelector(state => state.auth)
  const { activeSociety } = useSidebarContext()
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [showCompleteModal, setShowCompleteModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [completeForm, setCompleteForm] = useState({
    remaining_amount: '',
    receipt_no: '',
    payment_mode: 'Full',
    installment_years: '',
    installment_months: '',
    initial_payment: ''
  })
  const [editForm, setEditForm] = useState({
    plot_id: '',
    customer_id: '',
    total_amount: '',
    hold_amount: '',
    hold_days: '',
    receipt_no: '',
    dealer_name: '',
    dealer_phone: '',
    dealer_comission: '',
    dealer_commission_date: null,
    offertag: '',
    booking_date: null
  })
  const [printingBookingId, setPrintingBookingId] = useState(null)
  const [availablePlots, setAvailablePlots] = useState([])
  const [plotSearchTerm, setPlotSearchTerm] = useState('')
  const [customerSearchTerm, setCustomerSearchTerm] = useState('')
  const [showPlotDropdown, setShowPlotDropdown] = useState(false)
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false)

  // Filter hold bookings by active society
  const filteredHoldBookings =
    holdBookings?.filter(booking =>
      activeSociety
        ? booking.plot_id?.society_id?._id === activeSociety._id
        : true
    ) || []

  const filteredExpiredHoldBookings =
    expiredHoldBookings?.filter(booking =>
      activeSociety
        ? booking.plot_id?.society_id?._id === activeSociety._id
        : true
    ) || []

  useEffect(() => {
    dispatch(getHoldBookings())
    dispatch(getExpiredHoldBookings())
    dispatch(fetchAllUsers())
  }, [dispatch])

  // Fetch all plots when active society changes or when editing
  useEffect(() => {
    const fetchPlots = async () => {
      if (!activeSociety) {
        setAvailablePlots([])
        return
      }
      try {
        const response = await api.get(`/societies/${activeSociety._id}`)
        if (response.data.society && response.data.society.plots) {
          // Get ALL plots from database (not filtered)
          const allPlots = response.data.society.plots || []
          
          // If we're editing a booking, ensure the current plot is included
          if (selectedBooking && selectedBooking.plot_id) {
            const currentPlotId = selectedBooking.plot_id._id || selectedBooking.plot_id
            const currentPlot = allPlots.find(p => p._id === currentPlotId)
            if (currentPlot && !allPlots.some(p => p._id === currentPlotId)) {
              allPlots.push(currentPlot)
            }
          }
          
          // Sort plots by plot number
          const sortedPlots = [...allPlots].sort((a, b) => {
            if (a.plot_number && b.plot_number) {
              return a.plot_number.localeCompare(b.plot_number, undefined, { numeric: true, sensitivity: 'base' })
            }
            return 0
          })
          
          setAvailablePlots(sortedPlots)
        }
      } catch (error) {
        console.error('Error fetching plots:', error)
        setAvailablePlots([])
      }
    }
    fetchPlots()
  }, [activeSociety, selectedBooking])

  // Filter plots based on search term with enhanced search
  const filteredPlots = availablePlots.filter(plot => {
    if (!plotSearchTerm) return true
    const searchLower = plotSearchTerm.toLowerCase().trim()
    const plotNumber = plot.plot_number?.toString().toLowerCase() || ''
    const block = plot.block?.toString().toLowerCase() || ''
    const size = plot.size?.toString().toLowerCase() || ''
    const marla = plot.marla?.toString().toLowerCase() || ''
    const category = plot.category?.toString().toLowerCase() || ''
    const status = plot.booking_status?.toString().toLowerCase() || ''
    
    return (
      plotNumber.includes(searchLower) ||
      block.includes(searchLower) ||
      size.includes(searchLower) ||
      marla.includes(searchLower) ||
      category.includes(searchLower) ||
      status.includes(searchLower) ||
      `plot ${plotNumber}`.includes(searchLower) ||
      `block ${block}`.includes(searchLower)
    )
  })

  // Filter customers based on search term
  const filteredCustomers = users
    ?.filter(user => user.role === 'Customer')
    .filter(user => {
      if (!customerSearchTerm) return true
      const searchLower = customerSearchTerm.toLowerCase()
      const name =
        user.customerData?.name ||
        user.name ||
        user.username ||
        ''
      const phone =
        user.customerData?.contactInfo?.phone ||
        user.contactInfo?.phone ||
        ''
      return (
        name.toLowerCase().includes(searchLower) ||
        phone.includes(customerSearchTerm)
      )
    }) || []

  // Helper function to safely parse dates
  const safeParseDate = (dateValue) => {
    if (!dateValue) return null
    try {
      const date = new Date(dateValue)
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return null
      }
      return date
    } catch (error) {
      console.error('Error parsing date:', error, dateValue)
      return null
    }
  }

  const handleEditClick = booking => {
    setSelectedBooking(booking)
    setEditForm({
      plot_id: booking.plot_id?._id || booking.plot_id || '',
      customer_id: booking.customer_id?._id || booking.customer_id || '',
      total_amount: booking.total_amount?.toString() || '',
      hold_amount: booking.hold_amount?.toString() || '',
      hold_days: booking.hold_days?.toString() || '',
      receipt_no: booking.receipt_no || '',
      dealer_name: booking.dealer_name || '',
      dealer_phone: booking.dealer_phone || '',
      dealer_comission: booking.dealer_comission?.toString() || '',
      dealer_commission_date: safeParseDate(booking.dealer_commission_date),
      offertag: booking.offertag || '',
      booking_date: safeParseDate(booking.booking_date) || new Date()
    })
    setPlotSearchTerm(
      booking.plot_id?.plot_number
        ? `Plot ${booking.plot_id.plot_number} - Block ${booking.plot_id.block}`
        : ''
    )
    setCustomerSearchTerm(
      booking.customer_id?.name || booking.customer_id?.username || ''
    )
    setShowEditModal(true)
  }

  const handleEditSubmit = async () => {
    if (!selectedBooking) return

    // Validate form
    if (!editForm.plot_id) {
      enqueueSnackbar('Please select a plot', { variant: 'error' })
      return
    }
    if (!editForm.customer_id) {
      enqueueSnackbar('Please select a customer', { variant: 'error' })
      return
    }
    if (!editForm.total_amount || parseFloat(editForm.total_amount) <= 0) {
      enqueueSnackbar('Total amount must be greater than 0', {
        variant: 'error'
      })
      return
    }
    if (!editForm.hold_amount || parseFloat(editForm.hold_amount) <= 0) {
      enqueueSnackbar('Hold amount must be greater than 0', {
        variant: 'error'
      })
      return
    }
    if (parseFloat(editForm.hold_amount) >= parseFloat(editForm.total_amount)) {
      enqueueSnackbar('Hold amount must be less than total amount', {
        variant: 'error'
      })
      return
    }
    if (!editForm.hold_days || parseInt(editForm.hold_days) <= 0) {
      enqueueSnackbar('Hold days must be greater than 0', {
        variant: 'error'
      })
      return
    }

    try {
      // Safely format dates for API
      const formatDateForAPI = (date) => {
        if (!date) return undefined
        try {
          const d = date instanceof Date ? date : new Date(date)
          if (isNaN(d.getTime())) return undefined
          // Format as YYYY-MM-DD in local timezone to avoid UTC conversion
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        } catch {
          return undefined
        }
      }

      const payload = {
        bookingId: selectedBooking._id,
        plot_id: editForm.plot_id,
        customer_id: editForm.customer_id,
        total_amount: parseFloat(editForm.total_amount),
        hold_amount: parseFloat(editForm.hold_amount),
        hold_days: parseInt(editForm.hold_days),
        receipt_no: editForm.receipt_no,
        dealer_name: editForm.dealer_name,
        dealer_phone: editForm.dealer_phone,
        dealer_comission: editForm.dealer_comission
          ? parseFloat(editForm.dealer_comission)
          : undefined,
        dealer_commission_date: formatDateForAPI(editForm.dealer_commission_date),
        offertag: editForm.offertag,
        booking_date: formatDateForAPI(editForm.booking_date)
      }

      await dispatch(editHoldBooking(payload)).unwrap()

      enqueueSnackbar('Hold booking updated successfully!', {
        variant: 'success'
      })
      setShowEditModal(false)
      setSelectedBooking(null)
      setEditForm({
        plot_id: '',
        customer_id: '',
        total_amount: '',
        hold_amount: '',
        hold_days: '',
        receipt_no: '',
        dealer_name: '',
        dealer_phone: '',
        dealer_comission: '',
        dealer_commission_date: null,
        offertag: '',
        booking_date: null
      })
      setPlotSearchTerm('')
      setCustomerSearchTerm('')
      dispatch(getHoldBookings()) // Refresh the list
    } catch (error) {
      enqueueSnackbar(
        `Error: ${error.message || error.error || 'Failed to update hold booking'}`,
        {
          variant: 'error'
        }
      )
    }
  }

  const handleDeleteBooking = async booking => {
    if (!booking) return
  
    const result = await Swal.fire({
      title: 'Cancel Hold Booking?',
      html: `
        <div style="text-align: left; margin-top: 1rem;">
          <p style="margin-bottom: 0.5rem;"><strong>Plot:</strong> ${booking.plot_id?.plot_number}</p>
          <p style="margin-bottom: 0.5rem;"><strong>Customer:</strong> ${booking.customer_id?.name}</p>
          <p style="margin-top: 1rem; color: #fbbf24;">This will release the plot back to inventory and move the booking to expired/cancelled list.</p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Cancel Booking',
      cancelButtonText: 'No, Keep It',
      background: 'linear-gradient(to bottom right, rgb(30, 41, 59), rgb(15, 23, 42), rgb(30, 41, 59))',
      color: '#5eead4',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      customClass: {
        popup: 'border border-teal-300/20 rounded-xl shadow-2xl',
        title: 'text-teal-300 text-2xl font-bold',
        htmlContainer: 'text-teal-300',
        confirmButton: 'px-6 py-2 rounded-lg font-semibold',
        cancelButton: 'px-6 py-2 rounded-lg font-semibold'
      }
    })
  
    if (!result.isConfirmed) return
  
    try {
      await dispatch(deleteBooking(booking._id)).unwrap()
  
      Swal.fire({
        title: 'Success!',
        text: 'Hold booking cancelled successfully!',
        icon: 'success',
        background: 'linear-gradient(to bottom right, rgb(30, 41, 59), rgb(15, 23, 42), rgb(30, 41, 59))',
        color: '#5eead4',
        confirmButtonColor: '#059669',
        customClass: {
          popup: 'border border-teal-300/20 rounded-xl shadow-2xl',
          title: 'text-teal-300 text-2xl font-bold',
          confirmButton: 'px-6 py-2 rounded-lg font-semibold'
        }
      })
  
      // Refresh both lists to show the cancelled booking in expired section
      dispatch(getHoldBookings()) // Refresh active hold bookings
      dispatch(getExpiredHoldBookings()) // Refresh expired/cancelled list
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: error.message || error.error || 'Failed to cancel hold booking',
        icon: 'error',
        background: 'linear-gradient(to bottom right, rgb(30, 41, 59), rgb(15, 23, 42), rgb(30, 41, 59))',
        color: '#5eead4',
        confirmButtonColor: '#dc2626',
        customClass: {
          popup: 'border border-teal-300/20 rounded-xl shadow-2xl',
          title: 'text-teal-300 text-2xl font-bold',
          confirmButton: 'px-6 py-2 rounded-lg font-semibold'
        }
      })
    }
  }

  const handleCompleteBooking = async () => {
    if (!selectedBooking) return

    // Validate form based on payment mode
    if (completeForm.payment_mode === 'Installment') {
      if (
        completeForm.installment_years === '' ||
        completeForm.installment_years === null ||
        completeForm.installment_years === undefined ||
        completeForm.installment_months === '' ||
        completeForm.installment_months === null ||
        completeForm.installment_months === undefined ||
        !completeForm.initial_payment
      ) {
        enqueueSnackbar(
          'Please fill in installment years, months, and initial payment',
          {
            variant: 'error'
          }
        )
        return
      }

      // Validate installment years and months
      const years = parseInt(completeForm.installment_years)
      const months = parseInt(completeForm.installment_months)
      const initialPayment = parseFloat(completeForm.initial_payment)
      const remainingAmount = parseFloat(completeForm.remaining_amount)

      // New validation logic: Allow years=0 if months>0, or months=0 if years>=1
      if (years < 0 || years > 20) {
        enqueueSnackbar('Installment years must be between 0 and 20', {
          variant: 'error'
        })
        return
      }

      if (months < 0 || months > 11) {
        enqueueSnackbar('Installment months must be between 0 and 11', {
          variant: 'error'
        })
        return
      }

      // Ensure at least one of years or months is greater than 0
      if (years === 0 && months === 0) {
        enqueueSnackbar('Either years or months must be greater than 0', {
          variant: 'error'
        })
        return
      }

      // If years is 0, months must be greater than 0
      if (years === 0 && months <= 0) {
        enqueueSnackbar('If years is 0, months must be greater than 0', {
          variant: 'error'
        })
        return
      }

      // If months is 0, years must be at least 1
      if (months === 0 && years < 1) {
        enqueueSnackbar('If months is 0, years must be at least 1', {
          variant: 'error'
        })
        return
      }

      // Validate initial payment
      if (initialPayment <= 0) {
        enqueueSnackbar('Initial payment must be greater than 0', {
          variant: 'error'
        })
        return
      }

      if (initialPayment >= remainingAmount) {
        enqueueSnackbar(
          'Initial payment must be less than the remaining amount',
          {
            variant: 'error'
          }
        )
        return
      }

      // Check if total period is reasonable (not more than 20 years)
      const totalYears = years + months / 12
      if (totalYears > 20) {
        enqueueSnackbar('Total installment period cannot exceed 20 years', {
          variant: 'error'
        })
        return
      }
    }

    try {
      const payload = {
        bookingId: selectedBooking._id,
        remaining_amount: parseFloat(completeForm.remaining_amount),
        receipt_no: completeForm.receipt_no,
        payment_mode: completeForm.payment_mode
      }

      // Add installment details if payment mode is Installment
      if (completeForm.payment_mode === 'Installment') {
        payload.installment_years = parseInt(completeForm.installment_years)
        payload.installment_months = parseInt(completeForm.installment_months)
        payload.initial_payment = parseFloat(completeForm.initial_payment)
      }

      await dispatch(completeHoldBooking(payload)).unwrap()

      enqueueSnackbar(
        `Hold booking completed successfully as ${completeForm.payment_mode} payment!`,
        {
          variant: 'success'
        }
      )
      setShowCompleteModal(false)
      setSelectedBooking(null)
      setCompleteForm({
        remaining_amount: '',
        receipt_no: '',
        payment_mode: 'Full',
        installment_years: '',
        installment_months: '',
        initial_payment: ''
      })
      dispatch(getHoldBookings()) // Refresh the list
    } catch (error) {
      enqueueSnackbar(`Error: ${error.message || error}`, {
        variant: 'error'
      })
    }
  }

  const formatDate = dateString => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'N/A'
      }
      return date.toLocaleDateString('en-GB')
    } catch (error) {
      console.error('Error formatting date:', error, dateString)
      return 'N/A'
    }
  }

  const formatCurrency = amount => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR'
    }).format(amount)
  }

  const getDaysRemaining = expiryDate => {
    if (!expiryDate) return null
    try {
      const now = new Date()
      const expiry = new Date(expiryDate)
      // Check if date is valid
      if (isNaN(expiry.getTime())) {
        return null
      }
      const diffTime = expiry - now
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      return diffDays
    } catch (error) {
      console.error('Error calculating days remaining:', error, expiryDate)
      return null
    }
  }

  const handlePrintHoldSlip = booking => {
    // console.log("Print button clicked for booking:", booking);
    setPrintingBookingId(booking._id)

    const printContainer = document.createElement('div')
    printContainer.id = 'hold-slip-content'

    // Format currency for print
    const formatCurrencyForPrint = amount => {
      return new Intl.NumberFormat('en-PK', {
        style: 'currency',
        currency: 'PKR'
      }).format(amount || 0)
    }

    // Add print-specific styles
    const printStyles = `
    @media print {
      @page {
        size: A4;
        margin: 0;
        bleed: 0;
      }
      
      body {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    }
  `

    printContainer.innerHTML = `
    <style>${printStyles}</style>
    <div style="width: 210mm; height: 297mm; margin: 0; padding: 12mm 10mm; box-sizing: border-box; background-color: white; font-family: 'Inter', sans-serif; position: relative; color: #1a1a1a;">
      <!-- Header Background -->
      <div style="position: absolute; top: 0; left: 0; right: 0; height: 60mm; background: linear-gradient(135deg, #000428 0%, #004e92 100%); -webkit-print-color-adjust: exact;">
        <div style="position: absolute; bottom: 0; left: 0; right: 0; height: 20mm; background: linear-gradient(to bottom right, transparent 49.5%, white 50%);"></div>
      </div>

      <!-- Content Wrapper -->
      <div style="position: relative; height: 100%;">
        <!-- Header -->
        <div style="text-align: center; color: white; padding-top: 6mm; margin-bottom: 8mm;">
          <!-- Flex Container -->
          <div style="display: flex; justify-content: center; align-items: center; gap: 15mm; margin-bottom: 3mm;">
            <!-- Logo -->
            <div style="width: 20mm; height: 20mm; border-radius: 50%; overflow: hidden; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);">
              <img
                src="${booking.plot_id?.society_id?.society_image || 'N/A'}"
                alt="Logo"
                style="width: 100%; height: 100%; object-fit: cover;"
              />
            </div>
            <!-- Main Content -->
            <div>
              <h1
                style="
                  margin: 0;
                  font-size: 14pt;
                  font-weight: 900;
                  letter-spacing: 1px;
                  text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.5);
                  color: #FFD700;
                "
              >
                ${booking.plot_id?.society_id?.name || 'N/A'}
              </h1>
              <p
                style="
                  margin: 2mm 0 0;
                  font-size: 11pt;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                  color: #ddd;
                "
              >
                ${booking.plot_id?.society_id?.location || 'N/A'}
              </p>
              <p
                style="
                  margin: 1mm 0 0;
                  font-size: 11pt;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                  font-style: italic;
                  color: #ddd;
                "
              >
                Hold Booking Receipt
              </p>
            </div>
          </div>
        </div>

        <!-- Receipt Details -->
        <div style="background: white; border-radius: 3mm; padding: 3mm; box-shadow: 0 1mm 3mm rgba(0,0,0,0.1); margin-bottom: 3mm; page-break-inside: avoid;">
          <div style="display: flex; justify-content: space-between; align-items: center; font-size: 10pt; font-family: Arial, sans-serif;">
            <div style="flex: 1; text-align: left;">
              <span style="color: #666; font-weight: bold;">Receipt No:</span>
              <span style="font-weight: 300; margin-left: 3px;">${
                booking.receipt_no ||
                `HB-${Math.random().toString(36).substr(2, 8).toUpperCase()}`
              }</span>
            </div>
            <div style="flex: 1; text-align: center;">
              <span style="color: #666; font-weight: bold;">Hold Date:</span>
              <span style="font-weight: 300; margin-left: 3px;">${(() => {
                try {
                  const date = booking.booking_date ? new Date(booking.booking_date) : new Date()
                  return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-GB')
                } catch {
                  return 'N/A'
                }
              })()}</span>
            </div>
            <div style="flex: 1; text-align: right;">
              <span style="color: #666; font-weight: bold;">Expiry Date:</span>
              <span style="font-weight: 300; margin-left: 3px;">${(() => {
                try {
                  const date = booking.hold_expiry_date ? new Date(booking.hold_expiry_date) : null
                  return date && !isNaN(date.getTime()) ? date.toLocaleDateString('en-GB') : 'N/A'
                } catch {
                  return 'N/A'
                }
              })()}</span>
            </div>
          </div>
        </div>

        <!-- Main Content Grid -->
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 3mm; margin-bottom: 3mm;">
          <!-- Customer Details -->
          <div style="background: white; border-radius: 2mm; padding: 3mm; box-shadow: 0 0.5mm 2mm rgba(0,0,0,0.1);">
            <h2 style="margin: 0 0 2mm; font-size: 11pt; color: #000428;">Customer Information</h2>
            <table style="width: 100%; border-collapse: collapse;">
              <tr style="border-bottom: 0.2mm solid #eee;">
                <td style="padding: 1mm 0; color: #666; font-size: 9pt;">Name:</td>
                <td style="padding: 1mm 0; text-align: right; font-weight: 600; font-size: 9pt;">${
                  booking.customer_id?.name || 'N/A'
                }</td>
              </tr>
              <tr style="border-bottom: 0.2mm solid #eee;">
                <td style="padding: 1mm 0; color: #666; font-size: 9pt;">Phone:</td>
                <td style="padding: 1mm 0; text-align: right; font-weight: 600; font-size: 9pt;">${
                  booking.customer_id?.contactInfo?.phone || 'N/A'
                }</td>
              </tr>
              <tr style="border-bottom: 0.2mm solid #eee;">
                <td style="padding: 1mm 0; color: #666; font-size: 9pt;">CNIC:</td>
                <td style="padding: 1mm 0; text-align: right; font-weight: 600; font-size: 9pt;">${
                  booking.customer_id?.cnic || 'N/A'
                }</td>
              </tr>
              <tr>
                <td style="padding: 1mm 0; color: #666; font-size: 9pt;">Address:</td>
                <td style="padding: 1mm 0; text-align: right; font-weight: 600; font-size: 9pt;">${
                  booking.customer_id?.contactInfo?.address || 'N/A'
                }</td>
              </tr>
            </table>
          </div>

          <!-- Plot Details -->
          <div style="background: white; border-radius: 2mm; padding: 3mm; box-shadow: 0 0.5mm 2mm rgba(0,0,0,0.1);">
            <h2 style="margin: 0 0 2mm; font-size: 11pt; color: #000428;">Plot Information</h2>
            <table style="width: 100%; border-collapse: collapse;">
              <tr style="border-bottom: 0.2mm solid #eee;">
                <td style="padding: 1mm 0; color: #666; font-size: 9pt;">Plot Number:</td>
                <td style="padding: 1mm 0; text-align: right; font-weight: 600; font-size: 9pt;">${
                  booking.plot_id?.plot_number || 'N/A'
                }</td>
              </tr>
              <tr style="border-bottom: 0.2mm solid #eee;">
                <td style="padding: 1mm 0; color: #666; font-size: 9pt;">Block:</td>
                <td style="padding: 1mm 0; text-align: right; font-weight: 600; font-size: 9pt;">${
                  booking.plot_id?.block || 'N/A'
                }</td>
              </tr>
              <tr style="border-bottom: 0.2mm solid #eee;">
                <td style="padding: 1mm 0; color: #666; font-size: 9pt;">Size:</td>
                <td style="padding: 1mm 0; text-align: right; font-weight: 600; font-size: 9pt;">${
                  booking.plot_id?.size
                    ? `${booking.plot_id.size} sq.ft`
                    : 'N/A'
                }</td>
              </tr>
              <tr style="border-bottom: 0.2mm solid #eee;">
                <td style="padding: 1mm 0; color: #666; font-size: 9pt;">Marla:</td>
                <td style="padding: 1mm 0; text-align: right; font-weight: 600; font-size: 9pt;">${
                  booking.plot_id?.marla
                    ? `${booking.plot_id.marla} Marla`
                    : 'N/A'
                }</td>
              </tr>
              <tr>
                <td style="padding: 1mm 0; color: #666; font-size: 9pt;">Society:</td>
                <td style="padding: 1mm 0; text-align: right; font-weight: 600; font-size: 9pt;">${
                  booking.plot_id?.society_id?.name || 'N/A'
                }</td>
              </tr>
            </table>
          </div>
        </div>

        <!-- Financial Details -->
        <div style="background: white; border-radius: 2mm; padding: 3mm; box-shadow: 0 0.5mm 2mm rgba(0,0,0,0.1); margin-bottom: 3mm;">
          <h2 style="margin: 0 0 2mm; font-size: 11pt; color: #000428;">Financial Details</h2>
          <table style="width: 100%; border-collapse: collapse;">
            <tr style="border-bottom: 0.2mm solid #eee;">
              <td style="padding: 1mm 0; color: #666; font-size: 9pt;">Total Plot Amount:</td>
              <td style="padding: 1mm 0; text-align: right; font-weight: 600; font-size: 9pt;">${formatCurrencyForPrint(
                booking.total_amount
              )}</td>
            </tr>
            <tr style="border-bottom: 0.2mm solid #eee;">
              <td style="padding: 1mm 0; color: #666; font-size: 9pt;">Hold Amount Paid:</td>
              <td style="padding: 1mm 0; text-align: right; font-weight: 600; color: #28a745; font-size: 9pt;">${formatCurrencyForPrint(
                booking.hold_amount
              )}</td>
            </tr>
            <tr style="border-bottom: 0.2mm solid #eee;">
              <td style="padding: 1mm 0; color: #666; font-size: 9pt;">Remaining Balance:</td>
              <td style="padding: 1mm 0; text-align: right; font-weight: 600; color: #dc3545; font-size: 9pt;">${formatCurrencyForPrint(
                booking.remaining_balance
              )}</td>
            </tr>
            <tr style="background: #f8f9fa;">
              <td style="padding: 1mm 0; color: #000; font-weight: bold; font-size: 9pt;">Payment Status:</td>
              <td style="padding: 1mm 0; text-align: right; font-weight: bold; color: #28a745; font-size: 9pt;">HOLD BOOKING ACTIVE</td>
            </tr>
          </table>
        </div>

        <!-- Important Notice -->
        <div style="background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 2mm; padding: 2mm; margin-bottom: 3mm;">
          <h3 style="margin: 0 0 1mm; font-size: 10pt; color: #856404; font-family: Arial, sans-serif;">⚠️ IMPORTANT NOTICE</h3>
          <p style="margin: 0; color: #856404; font-size: 8pt; font-family: Arial, sans-serif; line-height: 1.4;">
            <strong>Days Remaining:</strong> ${
              getDaysRemaining(booking.hold_expiry_date) !== null
                ? getDaysRemaining(booking.hold_expiry_date) < 0
                  ? 'EXPIRED'
                  : `${getDaysRemaining(booking.hold_expiry_date)} days`
                : 'N/A'
            }<br>
            <strong>Action Required:</strong> Complete the remaining payment before expiry to secure your plot booking.
          </p>
        </div>

        <!-- Terms & Conditions -->
        <div style="background: white; border-radius: 2mm; padding: 3mm; box-shadow: 0 0.5mm 2mm rgba(0,0,0,0.1); margin-bottom: 3mm; page-break-inside: avoid;">
          <h2 style="margin: 0 0 2mm; font-size: 11pt; color: #000428; font-family: Arial, sans-serif;">Terms & Conditions</h2>
          <ol style="margin: 0; padding-left: 1em; color: #444; font-size: 7pt; line-height: 1.4; font-family: Arial, sans-serif; list-style-type: decimal;">
            <li style="margin-bottom: 3px; text-align: justify; word-wrap: break-word;">Hold booking valid until ${(() => {
              try {
                const date = booking.hold_expiry_date ? new Date(booking.hold_expiry_date) : null
                return date && !isNaN(date.getTime()) ? date.toLocaleDateString('en-GB') : 'N/A'
              } catch {
                return 'N/A'
              }
            })()}. Hold amount ${formatCurrencyForPrint(
      booking.hold_amount
    )} non-refundable.</li>
            <li style="margin-bottom: 3px; text-align: justify; word-wrap: break-word;">Remaining balance ${formatCurrencyForPrint(
              booking.remaining_balance
            )} must be paid before expiry date.</li>
            <li style="margin-bottom: 3px; text-align: justify; word-wrap: break-word;">Customer must provide required documentation for final booking approval.</li>
            <li style="margin-bottom: 3px; text-align: justify; word-wrap: break-word;">Society reserves right to cancel if terms not complied with.</li>
            <li style="margin-bottom: 3px; text-align: justify; word-wrap: break-word;">Receipt must be presented with valid ID for queries or completion.</li>
            <li style="margin-bottom: 0; text-align: justify; word-wrap: break-word;">Document valid only with authorized signatures and official stamp.</li>
          </ol>
        </div>

        <!-- Signatures -->
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 3mm; margin-top: 5mm;">
          <div style="text-align: center;">
            <div style="border-bottom: 0.3mm solid #000; height: 10mm; margin-bottom: 1mm;"></div>
            <p style="margin: 0; font-size: 8pt; font-weight: 600;">Customer Signature</p>
            <p style="margin: 0.5mm 0 0; font-size: 6pt; color: #666;">Name & Date</p>
          </div>
          <div style="text-align: center;">
            <div style="border-bottom: 0.3mm solid #000; height: 10mm; margin-bottom: 1mm;"></div>
            <p style="margin: 0; font-size: 8pt; font-weight: 600;">Society Representative</p>
            <p style="margin: 0.5mm 0 0; font-size: 6pt; color: #666;">Signature & Date</p>
            <div style="margin-top: 2mm;">
              <div style="width: 15mm; height: 15mm; border: 0.3mm dashed #000; border-radius: 50%; margin: 0 auto; display: flex; align-items: center; justify-content: center;">
                <span style="font-size: 6pt; color: #666;">Official Stamp</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div style="position: absolute; bottom: 3mm; left: 10mm; right: 10mm; text-align: center; border-top: 0.2mm solid #eee; padding-top: 2mm;">
          <p style="margin: 0; font-size: 6pt; color: #666; font-family: Arial, sans-serif;">
            This is a computer-generated receipt. For any queries, please contact ${
              booking.plot_id?.society_id?.name || 'Al-Noor Developers'
            } at the registered office.
          </p>
        </div>
      </div>
    </div>
  `

    document.body.appendChild(printContainer)

    try {
      // console.log("Attempting to print...");

      // Method 1: Try to open a new window and print
      const printWindow = window.open(
        '',
        '_blank',
        'width=800,height=600,scrollbars=yes,resizable=yes'
      )

      if (printWindow) {
        // console.log("New window opened successfully");

        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Hold Booking Receipt</title>
              <style>
                @media print {
                  @page { 
                    size: A4; 
                    margin: 0; 
                  }
                  body { 
                    -webkit-print-color-adjust: exact !important; 
                    print-color-adjust: exact !important; 
                  }
                }
              </style>
            </head>
            <body>
              ${printContainer.innerHTML}
            </body>
          </html>
        `)
        printWindow.document.close()

        // Wait for content to load then print
        setTimeout(() => {
          // console.log("Attempting to print in new window...");
          printWindow.focus()
          printWindow.print()

          // Close window after printing
          setTimeout(() => {
            printWindow.close()
          }, 1000)

          enqueueSnackbar('Print dialog opened successfully!', {
            variant: 'success'
          })
        }, 1500)
      } else {
        // console.log("Popup blocked, trying alternative method...");

        // Method 2: If popup is blocked, try printJS
        try {
          printJS({
            printable: 'hold-slip-content',
            type: 'html',
            documentTitle: 'Al-Noor Developers - Hold Booking Receipt',
            targetStyles: ['*']
          })
          // console.log("printJS called successfully");
          enqueueSnackbar('Print dialog opened successfully!', {
            variant: 'success'
          })
        } catch (printJSError) {
          console.warn('printJS also failed:', printJSError)

          // Method 3: Last resort - direct window.print()
          // console.log("Trying direct window.print()...");
          const originalContent = document.body.innerHTML
          document.body.innerHTML = printContainer.innerHTML

          setTimeout(() => {
            window.print()
            document.body.innerHTML = originalContent
            enqueueSnackbar('Print dialog opened successfully!', {
              variant: 'success'
            })
          }, 500)
        }
      }
    } catch (error) {
      console.error('Print error:', error)
      enqueueSnackbar('Error printing slip. Please try again.', {
        variant: 'error'
      })
    } finally {
      // Clean up after a delay to ensure print dialog has time to open
      setTimeout(() => {
        if (document.body.contains(printContainer)) {
          document.body.removeChild(printContainer)
        }
        setPrintingBookingId(null)
      }, 3000)
    }
  }

  const getStatusColor = expiryDate => {
    const daysRemaining = getDaysRemaining(expiryDate)
    if (daysRemaining === null) return 'text-gray-500'
    if (daysRemaining < 0) return 'text-red-500'
    if (daysRemaining <= 3) return 'text-orange-500'
    return 'text-green-500'
  }

  if (loading) {
    return (
      <div
        className={`flex items-center justify-center min-h-screen ${gradient}`}
      >
        <div
          className={`bg-black/40 border ${theme.tailwind.border5} px-8 py-6 rounded-2xl shadow-2xl`}
        >
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto'></div>
          <p className={`text-xl ${theme.tailwind.text9} mt-4 text-center`}>
            Loading hold bookings...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`p-8 py-12 px-6 ${gradient} rounded-2xl ${theme.tailwind.shadows} min-h-screen`}
    >
      <div className='max-w-7xl mx-auto space-y-8'>
        <div
          className={`${gradient} shadow-2xl rounded-2xl border-2 ${theme.tailwind.border6} p-10 transform transition-all hover:scale-[1] hover:shadow-3xl`}
        >
          <div className='flex justify-between items-center mb-8'>
            <h2
              className={`text-5xl font-bold text-transparent bg-clip-text ${theme.tailwind.text8} uppercase tracking-wide`}
            >
              Hold Bookings
            </h2>
            <div className={`flex items-center gap-2 ${theme.tailwind.text9}`}>
              <Clock size={24} />
              <span className='text-lg font-semibold'>
                {filteredHoldBookings?.length || 0} Active Holds
                {activeSociety && (
                  <span className='text-sm font-normal ml-2'>
                    in {activeSociety.name}
                  </span>
                )}
              </span>
            </div>
          </div>

          {filteredHoldBookings && filteredHoldBookings.length > 0 ? (
            <div className='grid gap-6'>
              {filteredHoldBookings.map(booking => {
                const daysRemaining = getDaysRemaining(booking.hold_expiry_date)
                const isExpired = daysRemaining !== null && daysRemaining < 0

                return (
                  <div
                    key={booking._id}
                    className={`${theme.tailwind.bg2} border-2 ${theme.tailwind.border7} rounded-xl p-6 shadow-lg transition-all hover:shadow-xl hover:scale-[1.02]`}
                  >
                    <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
                      {/* Plot Information */}
                      <div className='space-y-4'>
                        <div className='flex items-center gap-3'>
                          <MapPin
                            className={`${theme.tailwind.text9} w-5 h-5`}
                          />
                          <div>
                            <h3
                              className={`text-xl font-bold ${theme.tailwind.text9}`}
                            >
                              Plot {booking.plot_id?.plot_number}
                            </h3>
                            <p className={`${theme.tailwind.text} text-sm`}>
                              {booking.plot_id?.society_id?.name}
                            </p>
                          </div>
                        </div>

                        <div className='grid grid-cols-2 gap-4'>
                          <div>
                            <p className={`${theme.tailwind.text} text-sm`}>
                              Block
                            </p>
                            <p
                              className={`${theme.tailwind.text9} font-semibold`}
                            >
                              {booking.plot_id?.block}
                            </p>
                          </div>
                          <div>
                            <p className={`${theme.tailwind.text} text-sm`}>
                              Size
                            </p>
                            <p
                              className={`${theme.tailwind.text9} font-semibold`}
                            >
                              {booking.plot_id?.size} sq.ft
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Customer Information */}
                      <div className='space-y-4'>
                        <div className='flex items-center gap-3'>
                          <User className={`${theme.tailwind.text9} w-5 h-5`} />
                          <div>
                            <h4
                              className={`text-lg font-semibold ${theme.tailwind.text9}`}
                            >
                              {booking.customer_id?.name}
                            </h4>
                            <div className='flex items-center gap-2 text-sm'>
                              <Phone className='w-4 h-4' />
                              <span className={`${theme.tailwind.text}`}>
                                {booking.customer_id?.contactInfo?.phone}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <p className={`${theme.tailwind.text} text-sm`}>
                            Address
                          </p>
                          <p className={`${theme.tailwind.text9} text-sm`}>
                            {booking.customer_id?.contactInfo?.address || 'N/A'}
                          </p>
                        </div>
                      </div>

                      {/* Financial Information */}
                      <div className='space-y-4'>
                        <div className='flex items-center gap-3'>
                          <DollarSign
                            className={`${theme.tailwind.text9} w-5 h-5`}
                          />
                          <div>
                            <p className={`${theme.tailwind.text} text-sm`}>
                              Total Amount
                            </p>
                            <p
                              className={`text-xl font-bold ${theme.tailwind.text9}`}
                            >
                              {formatCurrency(booking.total_amount)}
                            </p>
                          </div>
                        </div>

                        <div className='grid grid-cols-2 gap-4'>
                          <div>
                            <p className={`${theme.tailwind.text} text-sm`}>
                              Hold Amount
                            </p>
                            <p
                              className={`${theme.tailwind.text9} font-semibold`}
                            >
                              {formatCurrency(booking.hold_amount)}
                            </p>
                          </div>
                          <div>
                            <p className={`${theme.tailwind.text} text-sm`}>
                              Remaining
                            </p>
                            <p
                              className={`${theme.tailwind.text9} font-semibold`}
                            >
                              {formatCurrency(booking.remaining_balance)}
                            </p>
                          </div>
                        </div>

                        <div className='flex items-center gap-3'>
                          <Calendar
                            className={`${theme.tailwind.text9} w-5 h-5`}
                          />
                          <div>
                            <p className={`${theme.tailwind.text} text-sm`}>
                              Expires
                            </p>
                            <p
                              className={`font-semibold ${getStatusColor(
                                booking.hold_expiry_date
                              )}`}
                            >
                              {formatDate(booking.hold_expiry_date)}
                              {daysRemaining !== null && (
                                <span className='ml-2 text-sm'>
                                  (
                                  {daysRemaining < 0
                                    ? 'Expired'
                                    : `${daysRemaining} days left`}
                                  )
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className='flex justify-end gap-4 mt-6 pt-6 border-t border-gray-600'>
                      <button
                        onClick={() => handlePrintHoldSlip(booking)}
                        disabled={printingBookingId === booking._id}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                          printingBookingId === booking._id
                            ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700 hover:scale-105'
                        }`}
                      >
                        {printingBookingId === booking._id ? (
                          <>
                            <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-white'></div>
                            Printing...
                          </>
                        ) : (
                          <>
                            <Printer size={20} />
                            Print Slip
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleEditClick(booking)}
                        disabled={isExpired}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                          isExpired
                            ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                            : 'bg-yellow-600 text-white hover:bg-yellow-700 hover:scale-105'
                        }`}
                      >
                        <Edit size={20} />
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          setSelectedBooking(booking)
                          setCompleteForm({
                            remaining_amount:
                              booking.remaining_balance.toString(),
                            receipt_no: '',
                            payment_mode: 'Full',
                            installment_years: '',
                            installment_months: '',
                            initial_payment: ''
                          })
                          setShowCompleteModal(true)
                        }}
                        disabled={isExpired}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                          isExpired
                            ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                            : 'bg-green-600 text-white hover:bg-green-700 hover:scale-105'
                        }`}
                      >
                        <CheckCircle size={20} />
                        Complete Booking
                      </button>
                      <button
                        onClick={() => handleDeleteBooking(booking)}
                        className='flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all bg-red-600 text-white hover:bg-red-700 hover:scale-105'
                      >
                        <Trash2 size={20} />
                        Delete
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div
              className={`text-center py-12 ${theme.tailwind.bg2} rounded-xl border-2 ${theme.tailwind.border7}`}
            >
              <AlertCircle
                className={`w-16 h-16 ${theme.tailwind.text9} mx-auto mb-4`}
              />
              <h3 className={`text-2xl font-bold ${theme.tailwind.text9} mb-2`}>
                No Hold Bookings Found
              </h3>
              <p className={`${theme.tailwind.text} text-lg`}>
                {activeSociety
                  ? `There are currently no active hold bookings in ${activeSociety.name}.`
                  : 'There are currently no active hold bookings in the system.'}
              </p>
            </div>
          )}
        </div>

        <div
          className={`${theme.tailwind.bg2} border-2 ${theme.tailwind.border6} rounded-2xl p-10 transform transition-all`}
        >
          <div className='flex justify-between items-center mb-8'>
            <h3
              className={`text-3xl font-bold ${theme.tailwind.text9} tracking-wide`}
            >
              Released / Expired Hold Bookings
            </h3>
            <div className={`flex items-center gap-2 ${theme.tailwind.text9}`}>
              <AlertCircle size={22} />
              <span className='text-base font-semibold'>
                {filteredExpiredHoldBookings?.length || 0} Released Holds
                {activeSociety && (
                  <span className='text-sm font-normal ml-2'>
                    in {activeSociety.name}
                  </span>
                )}
              </span>
            </div>
          </div>

          {filteredExpiredHoldBookings && filteredExpiredHoldBookings.length > 0 ? (
            <div className='grid gap-6'>
              {filteredExpiredHoldBookings.map(booking => (
                <div
                  key={booking._id}
                  className={`${theme.tailwind.bg5} border-2 ${theme.tailwind.border7} rounded-xl p-6 shadow-inner`}
                >
                  <div className='flex flex-col lg:flex-row justify-between gap-4 mb-4'>
                    <div>
                      <p className={`text-sm ${theme.tailwind.text}`}>Plot</p>
                      <p
                        className={`text-2xl font-bold ${theme.tailwind.text9}`}
                      >
                        Plot {booking.plot_id?.plot_number}
                      </p>
                      <p className={`${theme.tailwind.text} text-sm`}>
                        {booking.plot_id?.society_id?.name}
                      </p>
                    </div>
                    <div className='flex flex-col items-end gap-2'>
                      <span className='px-3 py-1 text-xs font-semibold rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'>
                        Released Back to Inventory
                      </span>
                      {booking.cancellation_reason && (
                        <span className='text-xs text-gray-400'>
                          {booking.cancellation_reason}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className='grid grid-cols-1 lg:grid-cols-4 gap-4'>
                    <div className={`${theme.tailwind.bg2} rounded-lg p-4`}>
                      <p className={`${theme.tailwind.text} text-sm`}>
                        Customer
                      </p>
                      <p
                        className={`text-lg font-semibold ${theme.tailwind.text9}`}
                      >
                        {booking.customer_id?.name}
                      </p>
                      <p className={`${theme.tailwind.text} text-sm`}>
                        {booking.customer_id?.contactInfo?.phone || 'N/A'}
                      </p>
                    </div>

                    <div className={`${theme.tailwind.bg2} rounded-lg p-4`}>
                      <p className={`${theme.tailwind.text} text-sm`}>
                        Hold Details
                      </p>
                      <p className={`text-sm ${theme.tailwind.text}`}>
                        Amount:{' '}
                        <span className='font-semibold'>
                          {formatCurrency(booking.hold_amount)}
                        </span>
                      </p>
                      <p className={`text-sm ${theme.tailwind.text}`}>
                        Hold Days:{' '}
                        <span className='font-semibold'>
                          {booking.hold_days || 'N/A'}
                        </span>
                      </p>
                    </div>

                    <div className={`${theme.tailwind.bg2} rounded-lg p-4`}>
                      <p className={`${theme.tailwind.text} text-sm`}>
                        Hold Period
                      </p>
                      <p className={`text-sm ${theme.tailwind.text}`}>
                        Started:{' '}
                        <span className='font-semibold'>
                          {formatDate(booking.booking_date)}
                        </span>
                      </p>
                      <p className={`text-sm ${theme.tailwind.text}`}>
                        Expired:{' '}
                        <span className='font-semibold'>
                          {formatDate(booking.hold_expiry_date)}
                        </span>
                      </p>
                    </div>

                    <div className={`${theme.tailwind.bg2} rounded-lg p-4`}>
                      <p className={`${theme.tailwind.text} text-sm`}>
                        Release Info
                      </p>
                      <p className={`text-sm ${theme.tailwind.text}`}>
                        Released On:{' '}
                        <span className='font-semibold'>
                          {formatDate(booking.cancellation_date)}
                        </span>
                      </p>
                      <p className={`text-sm ${theme.tailwind.text}`}>
                        Plot Status:{' '}
                        <span className='font-semibold'>
                          {booking.plot_id?.booking_status || 'Available'}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className={`text-center py-10 ${theme.tailwind.bg2} rounded-xl border-2 ${theme.tailwind.border7}`}
            >
              <AlertCircle
                className={`w-14 h-14 ${theme.tailwind.text} mx-auto mb-4`}
              />
              <h4 className={`text-xl font-semibold ${theme.tailwind.text9}`}>
                No Released Hold Bookings
              </h4>
              <p className={`${theme.tailwind.text} text-base`}>
                {activeSociety
                  ? `No hold bookings have expired in ${activeSociety.name} yet.`
                  : 'No hold bookings have expired yet.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Complete Booking Modal */}
      {showCompleteModal && selectedBooking && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
          <div
            className={`${theme.tailwind.bg2} border-2 ${theme.tailwind.border7} rounded-2xl p-8 max-w-lg w-full mx-4 shadow-2xl`}
          >
            <h3 className={`text-2xl font-bold ${theme.tailwind.text9} mb-6`}>
              Complete Hold Booking
            </h3>

            <div className='space-y-4'>
              <div>
                <label
                  className={`block ${theme.tailwind.text9} font-medium mb-2`}
                >
                  Payment Mode
                </label>
                <select
                  value={completeForm.payment_mode}
                  onChange={e =>
                    setCompleteForm(prev => ({
                      ...prev,
                      payment_mode: e.target.value
                    }))
                  }
                  className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black`}
                >
                  <option value='Full'>Full Payment</option>
                  <option value='Installment'>Installment Payment</option>
                </select>
              </div>

              <div>
                <label
                  className={`block ${theme.tailwind.text9} font-medium mb-2`}
                >
                  Remaining Amount
                </label>
                <input
                  type='number'
                  value={completeForm.remaining_amount}
                  onChange={e =>
                    setCompleteForm(prev => ({
                      ...prev,
                      remaining_amount: e.target.value
                    }))
                  }
                  className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black`}
                  placeholder='Enter remaining amount'
                />
              </div>

              <div>
                <label
                  className={`block ${theme.tailwind.text9} font-medium mb-2`}
                >
                  Receipt Number
                </label>
                <input
                  type='text'
                  value={completeForm.receipt_no}
                  onChange={e =>
                    setCompleteForm(prev => ({
                      ...prev,
                      receipt_no: e.target.value
                    }))
                  }
                  className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black`}
                  placeholder='Enter receipt number'
                />
              </div>

              {/* Installment Fields - Only show if payment mode is Installment */}
              {completeForm.payment_mode === 'Installment' && (
                <div className='space-y-4 border-t border-gray-600 pt-4'>
                  <h4
                    className={`text-lg font-semibold ${theme.tailwind.text9}`}
                  >
                    Installment Details
                  </h4>

                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <label
                        className={`block ${theme.tailwind.text9} font-medium mb-2`}
                      >
                        Years
                      </label>
                      <input
                        type='number'
                        min='1'
                        max='20'
                        value={completeForm.installment_years}
                        onChange={e =>
                          setCompleteForm(prev => ({
                            ...prev,
                            installment_years: e.target.value
                          }))
                        }
                        className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black`}
                        placeholder='Years'
                      />
                    </div>

                    <div>
                      <label
                        className={`block ${theme.tailwind.text9} font-medium mb-2`}
                      >
                        Months
                      </label>
                      <input
                        type='number'
                        min='0'
                        max='11'
                        value={completeForm.installment_months}
                        onChange={e =>
                          setCompleteForm(prev => ({
                            ...prev,
                            installment_months: e.target.value
                          }))
                        }
                        className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black`}
                        placeholder='Months'
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      className={`block ${theme.tailwind.text9} font-medium mb-2`}
                    >
                      Initial Payment
                    </label>
                    <input
                      type='number'
                      min='0'
                      step='0.01'
                      value={completeForm.initial_payment}
                      onChange={e =>
                        setCompleteForm(prev => ({
                          ...prev,
                          initial_payment: e.target.value
                        }))
                      }
                      className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black`}
                      placeholder='Enter initial payment amount'
                    />
                  </div>

                  {/* Show total period and monthly installment info */}
                  {completeForm.installment_years &&
                    completeForm.installment_months && (
                      <div
                        className={`p-3 ${theme.tailwind.bg5} rounded-lg border ${theme.tailwind.border10}`}
                      >
                        <p className={`text-sm ${theme.tailwind.text9}`}>
                          <strong>Total Period:</strong>{' '}
                          {completeForm.installment_years} years,{' '}
                          {completeForm.installment_months} months (
                          {parseFloat(completeForm.installment_years) +
                            parseFloat(completeForm.installment_months) /
                              12}{' '}
                          years total)
                        </p>
                        {completeForm.remaining_amount &&
                          completeForm.initial_payment && (
                            <p
                              className={`text-sm ${theme.tailwind.text9} mt-1`}
                            >
                              <strong>Monthly Installment:</strong>{' '}
                              {formatCurrency(
                                (parseFloat(completeForm.remaining_amount) -
                                  parseFloat(completeForm.initial_payment)) /
                                  (parseFloat(completeForm.installment_years) *
                                    12 +
                                    parseFloat(completeForm.installment_months))
                              )}
                            </p>
                          )}
                      </div>
                    )}
                </div>
              )}
            </div>

            <div className='flex justify-end gap-4 mt-8'>
              <button
                onClick={() => {
                  setShowCompleteModal(false)
                  setSelectedBooking(null)
                  setCompleteForm({
                    remaining_amount: '',
                    receipt_no: '',
                    payment_mode: 'Full',
                    installment_years: '',
                    installment_months: '',
                    initial_payment: ''
                  })
                }}
                className='px-6 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors'
              >
                Cancel
              </button>
              <button
                onClick={handleCompleteBooking}
                disabled={
                  !completeForm.remaining_amount ||
                  !completeForm.receipt_no ||
                  (completeForm.payment_mode === 'Installment' &&
                    (!completeForm.installment_years ||
                      !completeForm.installment_months ||
                      !completeForm.initial_payment))
                }
                className='px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors'
              >
                Complete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Hold Booking Modal */}
      {showEditModal && selectedBooking && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto'>
          <div
            className={`${theme.tailwind.bg2} border-2 ${theme.tailwind.border7} rounded-2xl p-8 max-w-2xl w-full mx-4 my-8 shadow-2xl`}
          >
            <h3 className={`text-2xl font-bold ${theme.tailwind.text9} mb-6`}>
              Edit Hold Booking
            </h3>

            <div className='space-y-4 max-h-[70vh] overflow-y-auto pr-2'>
              {/* Plot Selection */}
              <div className='relative'>
                <label
                  className={`block ${theme.tailwind.text9} font-medium mb-2`}
                >
                  Plot <span className='text-red-400'>*</span>
                </label>
                <div className='relative'>
                  <input
                    type='text'
                    value={plotSearchTerm}
                    onChange={e => {
                      setPlotSearchTerm(e.target.value)
                      setShowPlotDropdown(true)
                    }}
                    onFocus={() => setShowPlotDropdown(true)}
                    onBlur={() => {
                      // Delay closing to allow click events
                      setTimeout(() => setShowPlotDropdown(false), 200)
                    }}
                    placeholder='Search plot by number, block, size, marla, or category...'
                    className={`w-full px-4 py-2 pr-10 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black`}
                  />
                  <MapPin
                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${theme.tailwind.text} pointer-events-none`}
                  />
    </div>
                {showPlotDropdown && (
                  <div
                    className={`absolute z-50 w-full mt-2 ${theme.tailwind.bg2} border-2 ${theme.tailwind.border7} rounded-xl shadow-2xl max-h-80 overflow-y-auto backdrop-blur-xl`}
                    style={{ maxHeight: '320px' }}
                    onMouseDown={e => e.preventDefault()} // Prevent blur when clicking inside dropdown
                  >
                    {(() => {
                      const plotsToShow = plotSearchTerm ? filteredPlots : availablePlots
                      return plotsToShow.length > 0 ? (
                        <>
                          <div className={`px-4 py-2 ${theme.tailwind.bg3} border-b ${theme.tailwind.border7} sticky top-0`}>
                            <p className={`${theme.tailwind.text9} text-sm font-semibold`}>
                              {plotSearchTerm 
                                ? `${filteredPlots.length} plot${filteredPlots.length !== 1 ? 's' : ''} found`
                                : `${availablePlots.length} plot${availablePlots.length !== 1 ? 's' : ''} available`
                              }
                            </p>
                          </div>
                          {plotsToShow.map(plot => {
                          const isSelected = editForm.plot_id === plot._id
                          const isCurrentPlot = selectedBooking && 
                            (selectedBooking.plot_id?._id === plot._id || selectedBooking.plot_id === plot._id)
                          const statusColors = {
                            Available: 'bg-green-500/20 text-green-400 border-green-500/30',
                            Hold: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
                            Booked: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
                            Sold: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
                            Transfer: 'bg-orange-500/20 text-orange-400 border-orange-500/30'
                          }
                          const statusColor = statusColors[plot.booking_status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                          
                          return (
                            <div
                              key={plot._id}
                              onClick={() => {
                                setEditForm(prev => ({ ...prev, plot_id: plot._id }))
                                setPlotSearchTerm(
                                  `Plot ${plot.plot_number} - Block ${plot.block}${plot.marla ? ` (${plot.marla} Marla)` : ''}`
                                )
                                setShowPlotDropdown(false)
                              }}
                              className={`px-4 py-3 cursor-pointer border-b ${theme.tailwind.border7} last:border-none transition-all ${
                                isSelected
                                  ? `${theme.tailwind.bg3} border-l-4 border-teal-500`
                                  : `hover:${theme.tailwind.bg3}`
                              }`}
                            >
                              <div className='flex items-start justify-between gap-2'>
                                <div className='flex-1'>
                                  <div className='flex items-center gap-2 mb-1'>
                                    <p className={`${theme.tailwind.text9} font-semibold text-base`}>
                                      Plot {plot.plot_number}
                                    </p>
                                    {isCurrentPlot && (
                                      <span className='px-2 py-0.5 text-xs bg-teal-500/20 text-teal-400 border border-teal-500/30 rounded'>
                                        Current
                                      </span>
                                    )}
                                    {isSelected && (
                                      <span className='px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded'>
                                        Selected
                                      </span>
                                    )}
                                  </div>
                                  <div className='flex flex-wrap items-center gap-2 text-sm'>
                                    <span className={`${theme.tailwind.text}`}>
                                      Block {plot.block}
                                    </span>
                                    {plot.size && (
                                      <span className={`${theme.tailwind.text}`}>
                                        • {plot.size} sq.ft
                                      </span>
                                    )}
                                    {plot.marla && (
                                      <span className={`${theme.tailwind.text}`}>
                                        • {plot.marla} Marla
                                      </span>
                                    )}
                                    {plot.category && (
                                      <span className={`${theme.tailwind.text}`}>
                                        • {plot.category}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded border ${statusColor} whitespace-nowrap`}
                                >
                                  {plot.booking_status || 'N/A'}
                                </span>
                              </div>
                            </div>
                          )
                        })}
                        </>
                      ) : null
                    })()}
                    {plotSearchTerm && filteredPlots.length === 0 && (
                      <div className={`px-4 py-8 text-center ${theme.tailwind.text}`}>
                        <MapPin className={`w-12 h-12 mx-auto mb-2 ${theme.tailwind.text} opacity-50`} />
                        <p className={`${theme.tailwind.text9} font-medium`}>
                          No plots found
                        </p>
                        <p className={`${theme.tailwind.text} text-sm mt-1`}>
                          Try a different search term
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Customer Selection */}
              <div className='relative'>
                <label
                  className={`block ${theme.tailwind.text9} font-medium mb-2`}
                >
                  Customer
                </label>
                <input
                  type='text'
                  value={customerSearchTerm}
                  onChange={e => {
                    setCustomerSearchTerm(e.target.value)
                    setShowCustomerDropdown(true)
                  }}
                  onFocus={() => setShowCustomerDropdown(true)}
                  placeholder='Search customer by name or phone'
                  className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black`}
                />
                {showCustomerDropdown && filteredCustomers.length > 0 && (
                  <div
                    className={`absolute z-50 w-full mt-2 ${theme.tailwind.bg2} border ${theme.tailwind.border7} rounded-xl shadow-2xl max-h-60 overflow-y-auto`}
                  >
                    {filteredCustomers.map(user => {
                      const name =
                        user.customerData?.name ||
                        user.name ||
                        user.username ||
                        ''
                      const phone =
                        user.customerData?.contactInfo?.phone ||
                        user.contactInfo?.phone ||
                        ''
                      return (
                        <div
                          key={user._id}
                          onClick={() => {
                            setEditForm(prev => ({
                              ...prev,
                              customer_id: user._id
                            }))
                            setCustomerSearchTerm(name)
                            setShowCustomerDropdown(false)
                          }}
                          className={`px-4 py-3 hover:${theme.tailwind.bg3} cursor-pointer border-b ${theme.tailwind.border7} last:border-none transition-colors`}
                        >
                          <p className={`${theme.tailwind.text9} font-medium`}>
                            {name}
                          </p>
                          {phone && (
                            <p className={`${theme.tailwind.text} text-sm`}>
                              {phone}
                            </p>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Total Amount */}
              <div>
                <label
                  className={`block ${theme.tailwind.text9} font-medium mb-2`}
                >
                  Total Amount
                </label>
                <input
                  type='number'
                  value={editForm.total_amount}
                  onChange={e =>
                    setEditForm(prev => ({
                      ...prev,
                      total_amount: e.target.value
                    }))
                  }
                  className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black`}
                  placeholder='Enter total amount'
                />
              </div>

              {/* Hold Amount */}
              <div>
                <label
                  className={`block ${theme.tailwind.text9} font-medium mb-2`}
                >
                  Hold Amount
                </label>
                <input
                  type='number'
                  value={editForm.hold_amount}
                  onChange={e =>
                    setEditForm(prev => ({
                      ...prev,
                      hold_amount: e.target.value
                    }))
                  }
                  className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black`}
                  placeholder='Enter hold amount'
                />
              </div>

              {/* Hold Days */}
              <div>
                <label
                  className={`block ${theme.tailwind.text9} font-medium mb-2`}
                >
                  Hold Days
                </label>
                <input
                  type='number'
                  value={editForm.hold_days}
                  onChange={e =>
                    setEditForm(prev => ({
                      ...prev,
                      hold_days: e.target.value
                    }))
                  }
                  className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black`}
                  placeholder='Enter hold days'
                />
              </div>

              {/* Receipt Number */}
              <div>
                <label
                  className={`block ${theme.tailwind.text9} font-medium mb-2`}
                >
                  Receipt Number
                </label>
                <input
                  type='text'
                  value={editForm.receipt_no}
                  onChange={e =>
                    setEditForm(prev => ({
                      ...prev,
                      receipt_no: e.target.value
                    }))
                  }
                  className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black`}
                  placeholder='Enter receipt number'
                />
              </div>

              {/* Dealer Information */}
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label
                    className={`block ${theme.tailwind.text9} font-medium mb-2`}
                  >
                    Dealer Name
                  </label>
                  <input
                    type='text'
                    value={editForm.dealer_name}
                    onChange={e =>
                      setEditForm(prev => ({
                        ...prev,
                        dealer_name: e.target.value
                      }))
                    }
                    className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black`}
                    placeholder='Dealer name'
                  />
                </div>
                <div>
                  <label
                    className={`block ${theme.tailwind.text9} font-medium mb-2`}
                  >
                    Dealer Phone
                  </label>
                  <input
                    type='text'
                    value={editForm.dealer_phone}
                    onChange={e =>
                      setEditForm(prev => ({
                        ...prev,
                        dealer_phone: e.target.value
                      }))
                    }
                    className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black`}
                    placeholder='Dealer phone'
                  />
                </div>
              </div>

              {/* Dealer Commission */}
              <div>
                <label
                  className={`block ${theme.tailwind.text9} font-medium mb-2`}
                >
                  Dealer Commission
                </label>
                <input
                  type='number'
                  value={editForm.dealer_comission}
                  onChange={e =>
                    setEditForm(prev => ({
                      ...prev,
                      dealer_comission: e.target.value
                    }))
                  }
                  className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black`}
                  placeholder='Enter dealer commission'
                />
              </div>

              {/* Dealer Commission Date */}
              <div>
                <label
                  className={`block ${theme.tailwind.text9} font-medium mb-2`}
                >
                  Dealer Commission Date
                </label>
                <DatePicker
                  selected={editForm.dealer_commission_date && !isNaN(new Date(editForm.dealer_commission_date).getTime()) 
                    ? editForm.dealer_commission_date 
                    : null}
                  onChange={date =>
                    setEditForm(prev => ({
                      ...prev,
                      dealer_commission_date: date
                    }))
                  }
                  dateFormat='dd/MM/yyyy'
                  className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black`}
                  placeholderText='Select commission date'
                  isClearable
                />
              </div>

              {/* Offer Tag */}
              <div>
                <label
                  className={`block ${theme.tailwind.text9} font-medium mb-2`}
                >
                  Offer Tag
                </label>
                <input
                  type='text'
                  value={editForm.offertag}
                  onChange={e =>
                    setEditForm(prev => ({
                      ...prev,
                      offertag: e.target.value
                    }))
                  }
                  className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black`}
                  placeholder='Enter offer tag'
                />
              </div>

              {/* Booking Date */}
              <div>
                <label
                  className={`block ${theme.tailwind.text9} font-medium mb-2`}
                >
                  Booking Date
                </label>
                <DatePicker
                  selected={editForm.booking_date && !isNaN(new Date(editForm.booking_date).getTime()) 
                    ? editForm.booking_date 
                    : new Date()}
                  onChange={date =>
                    setEditForm(prev => ({
                      ...prev,
                      booking_date: date || new Date()
                    }))
                  }
                  dateFormat='dd/MM/yyyy'
                  className={`w-full px-4 py-2 border ${theme.tailwind.text9} ${theme.tailwind.bg5} ${theme.tailwind.border10} rounded-md focus:outline-none focus:ring-2 ${theme.tailwind.focusRing2} focus:bg-white focus:text-black`}
                  placeholderText='Select booking date'
                />
              </div>
            </div>

            <div className='flex justify-end gap-4 mt-8'>
              <button
                onClick={() => {
                  setShowEditModal(false)
                  setSelectedBooking(null)
                  setEditForm({
                    plot_id: '',
                    customer_id: '',
                    total_amount: '',
                    hold_amount: '',
                    hold_days: '',
                    receipt_no: '',
                    dealer_name: '',
                    dealer_phone: '',
                    dealer_comission: '',
                    dealer_commission_date: null,
                    offertag: '',
                    booking_date: null
                  })
                  setPlotSearchTerm('')
                  setCustomerSearchTerm('')
                }}
                className='px-6 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors'
              >
                Cancel
              </button>
              <button
                onClick={handleEditSubmit}
                disabled={
                  !editForm.plot_id ||
                  !editForm.customer_id ||
                  !editForm.total_amount ||
                  !editForm.hold_amount ||
                  !editForm.hold_days
                }
                className='px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors'
              >
                Update Booking
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default HoldBookings
